/**
 * Gesetzliche Feiertage in Deutschland (bundesweit + je Bundesland), lokal berechnet.
 *
 * Genutzt für die Sicherheitsabfrage „Eintrag am Wochenende/Feiertag anlegen?“ (Zeiterfassung,
 * Termine) und für „nächster Werktag“ beim Duplizieren. Das Bundesland kommt aus den
 * Kalendereinstellungen (/calendar/config, Feld „bundesland“, z. B. „BW“); ohne Bundesland
 * zählen nur die bundesweiten Feiertage.
 */
import api from '../services/api';

const pad = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const plusTage = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/** Ostersonntag (Gauß/Meeus) */
function ostersonntag(jahr) {
  const a = jahr % 19; const b = Math.floor(jahr / 100); const c = jahr % 100;
  const d = Math.floor(b / 4); const e = b % 4; const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3); const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4); const k = c % 4; const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const monat = Math.floor((h + l - 7 * m + 114) / 31);
  const tag = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(jahr, monat - 1, tag);
}

const cache = {};

/** Map JJJJ-MM-TT → Name für ein Jahr und Bundesland */
export function feiertageImJahr(jahr, bundesland = '') {
  const key = `${jahr}-${bundesland}`;
  if (cache[key]) return cache[key];
  const bl = String(bundesland || '').toUpperCase();
  const in_ = (...laender) => laender.includes(bl);
  const os = ostersonntag(jahr);
  const liste = [
    [new Date(jahr, 0, 1), 'Neujahr'],
    [plusTage(os, -2), 'Karfreitag'],
    [plusTage(os, 1), 'Ostermontag'],
    [new Date(jahr, 4, 1), 'Tag der Arbeit'],
    [plusTage(os, 39), 'Christi Himmelfahrt'],
    [plusTage(os, 50), 'Pfingstmontag'],
    [new Date(jahr, 9, 3), 'Tag der Deutschen Einheit'],
    [new Date(jahr, 11, 25), '1. Weihnachtstag'],
    [new Date(jahr, 11, 26), '2. Weihnachtstag'],
  ];
  if (in_('BW', 'BY', 'ST')) liste.push([new Date(jahr, 0, 6), 'Heilige Drei Könige']);
  if (in_('BE') || (in_('MV') && jahr >= 2023)) liste.push([new Date(jahr, 2, 8), 'Internationaler Frauentag']);
  if (in_('BW', 'BY', 'HE', 'NW', 'RP', 'SL')) liste.push([plusTage(os, 60), 'Fronleichnam']);
  if (in_('SL')) liste.push([new Date(jahr, 7, 15), 'Mariä Himmelfahrt']);
  if (in_('TH') && jahr >= 2019) liste.push([new Date(jahr, 8, 20), 'Weltkindertag']);
  if (in_('BB', 'HB', 'HH', 'MV', 'NI', 'SN', 'ST', 'SH', 'TH')) liste.push([new Date(jahr, 9, 31), 'Reformationstag']);
  if (in_('BW', 'BY', 'NW', 'RP', 'SL')) liste.push([new Date(jahr, 10, 1), 'Allerheiligen']);
  if (in_('SN')) {
    // Buß- und Bettag: Mittwoch vor dem 23. November
    const d = new Date(jahr, 10, 22);
    while (d.getDay() !== 3) d.setDate(d.getDate() - 1);
    liste.push([d, 'Buß- und Bettag']);
  }
  const map = {};
  liste.forEach(([d, name]) => { map[iso(d)] = name; });
  cache[key] = map;
  return map;
}

const datumAus = (wert) => {
  if (!wert) return null;
  if (wert instanceof Date) return new Date(wert.getFullYear(), wert.getMonth(), wert.getDate());
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(wert));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

/** Feiertagsname oder null */
export function feiertagName(wert, bundesland = '') {
  const d = datumAus(wert);
  return d ? (feiertageImJahr(d.getFullYear(), bundesland)[iso(d)] || null) : null;
}

/** „Samstag“, „Sonntag“, „Feiertag (Name)“ oder null */
export function freierTag(wert, bundesland = '') {
  const d = datumAus(wert);
  if (!d) return null;
  const f = feiertagName(d, bundesland);
  if (f) return `Feiertag (${f})`;
  if (d.getDay() === 6) return 'Samstag';
  if (d.getDay() === 0) return 'Sonntag';
  return null;
}

/** Tage bis zum nächsten Arbeitstag (kein Wochenende, kein Feiertag) */
export function tageBisNaechsterArbeitstag(wert, bundesland = '') {
  const d = datumAus(wert);
  if (!d) return 1;
  let tage = 0;
  do { d.setDate(d.getDate() + 1); tage += 1; } while (freierTag(d, bundesland) && tage < 14);
  return tage;
}

/**
 * Sicherheitsabfrage vor dem Speichern: true = speichern.
 * Fragt nur, wenn das Datum auf ein Wochenende oder einen gesetzlichen Feiertag fällt.
 */
export function wochenendeFeiertagBestaetigen(wert, bundesland = '', was = 'Eintrag') {
  const frei = freierTag(wert, bundesland);
  if (!frei) return true;
  const d = datumAus(wert);
  const datum = d.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
  return window.confirm(`${datum} ist ein ${frei.startsWith('Feiertag') ? 'gesetzlicher ' : ''}${frei}.\n\n${was} trotzdem für diesen Tag anlegen?`);
}

// Bundesland aus den Kalendereinstellungen (einmal laden, dann gemerkt)
let bundeslandCache = null;
let ladeVersprechen = null;
export function aktuellesBundesland() { return bundeslandCache || ''; }
export function bundeslandLaden() {
  if (bundeslandCache !== null) return Promise.resolve(bundeslandCache);
  if (!ladeVersprechen) {
    ladeVersprechen = api.get('/calendar/config')
      .then((r) => { bundeslandCache = r.data?.bundesland || ''; return bundeslandCache; })
      .catch(() => { bundeslandCache = ''; return ''; });
  }
  return ladeVersprechen;
}
